import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useLocale } from '../contexts/LocaleContext';
import { tokens } from '../theme/tokens';
import {
  ComparisonCell,
  ComparisonRow,
  MAX_COMPARE_PRODUCTS,
  MIN_COMPARE_PRODUCTS,
  ProductComparison,
} from '../types/productComparison';
import {
  DataTableShell,
  EmptyState,
  InlineMessage,
  LoadingState,
  PageContainer,
  PageHeader,
  StatusChip,
} from '../components/ui';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const rowKeyForI18n = (key: string): string => key.replace(/\./g, '_');

const parseIds = (raw: string | null): string[] =>
  Array.from(new Set((raw || '').split(',').map((v) => v.trim()).filter(Boolean))).slice(0, MAX_COMPARE_PRODUCTS);

/**
 * Store shell (Jordan): the product comparison table, opened from the compare
 * bar in the product browser at `/app/compare?ids=a,b`. Served by
 * `GET /store/compare`; rows, labels and "better" come from the API's registry.
 */
const ProductComparePage: React.FC = () => {
  const { t } = useTranslation() as { t: Translate };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEnabled, isLoading: flagsLoading } = useOrgFeatureFlags();
  const enabled = isEnabled('product_comparison');
  const { formatCurrency } = useLocale();
  const [differencesOnly, setDifferencesOnly] = useState(false);

  const ids = useMemo(() => parseIds(searchParams.get('ids')), [searchParams]);
  const canCompare = ids.length >= MIN_COMPARE_PRODUCTS;

  const { data, isLoading, error } = useQuery<ProductComparison, Error>({
    queryKey: ['product-compare', ids],
    enabled: enabled && canCompare,
    retry: false,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/compare?ids=${encodeURIComponent(ids.join(','))}`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error?.message || body?.message;
        throw new Error(res.status === 404 ? t('productCompare.page.notFound') : message || t('productCompare.page.loadError'));
      }
      return (await res.json()).data as ProductComparison;
    },
  });

  const removeProduct = (id: string) => {
    const remaining = ids.filter((i) => i !== id);
    if (remaining.length < MIN_COMPARE_PRODUCTS) {
      navigate('/app/products');
      return;
    }
    setSearchParams({ ids: remaining.join(',') }, { replace: true });
  };

  const rowLabel = (row: ComparisonRow) => t(`productCompare.rows.${rowKeyForI18n(row.key)}`, { defaultValue: row.label });

  const cellText = (row: ComparisonRow, cell: ComparisonCell): string => {
    if (row.kind === 'price') {
      if (cell.value === null) return t('productCompare.page.missing');
      const currency = cell.currency === 'CAD' || cell.currency === 'USD' ? cell.currency : undefined;
      return formatCurrency(cell.value, currency);
    }
    if (cell.display === null) return t('productCompare.page.missing');
    if (row.kind === 'boolean' && cell.value !== null) return t(cell.value === 1 ? 'productCompare.page.yes' : 'productCompare.page.no');
    return cell.display;
  };

  if (!flagsLoading && !enabled) {
    return (
      <PageContainer>
        <EmptyState title={t('productCompare.page.notAvailableTitle')} description={t('productCompare.page.notAvailableBody')} />
      </PageContainer>
    );
  }

  const visibleRows = (data?.rows ?? []).filter((row) => !differencesOnly || (row.differs && !row.identifier));
  const hasPrice = (data?.rows ?? []).some((row) => row.kind === 'price');

  return (
    <PageContainer>
      <PageHeader
        title={t('productCompare.title')}
        description={t('productCompare.description')}
        backTo="/app/products"
        backLabel={t('productCompare.page.back')}
        actions={
          <FormControlLabel
            control={<Checkbox size="small" checked={differencesOnly} onChange={(e) => setDifferencesOnly(e.target.checked)} />}
            label={t('productCompare.page.differencesOnly')}
          />
        }
      />

      {!canCompare && (
        <EmptyState
          title={t('productCompare.page.needTwo')}
          actionLabel={t('productCompare.page.back')}
          onAction={() => navigate('/app/products')}
        />
      )}

      {canCompare && isLoading && <LoadingState message={t('common.loading')} />}
      {canCompare && error && <InlineMessage severity="error">{error.message}</InlineMessage>}

      {data && (
        <>
          {hasPrice && <InlineMessage severity="info">{t('productCompare.page.priceNote')}</InlineMessage>}
          <DataTableShell>
            <Table size="small" sx={{ minWidth: 120 + data.products.length * 200 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', width: 180, minWidth: 140, zIndex: 1 }}
                  />
                  {data.products.map((product) => (
                    <TableCell key={product.id} sx={{ verticalAlign: 'top', minWidth: 180 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {product.imageUrl && (
                            <Box
                              component="img"
                              src={product.imageUrl}
                              alt=""
                              loading="lazy"
                              sx={{ display: 'block', maxWidth: '100%', maxHeight: 96, objectFit: 'contain', mb: 1 }}
                            />
                          )}
                          <Link
                            component={RouterLink}
                            to={`/app/products?productId=${encodeURIComponent(product.id)}`}
                            underline="hover"
                            sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                            title={t('productCompare.page.viewProduct')}
                          >
                            {product.name}
                          </Link>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {[product.brand, product.model].filter(Boolean).join(' · ')}
                          </Typography>
                          {product.lifecycleStage && (
                            <StatusChip
                              size="small"
                              tone={product.lifecycleStage === 'preorder' ? 'caution' : 'info'}
                              label={t(product.lifecycleStage === 'preorder' ? 'productCompare.page.lifecyclePreorder' : 'productCompare.page.lifecycleAnnounced')}
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                        <Tooltip title={t('productCompare.page.removeProduct')}>
                          <IconButton
                            size="small"
                            aria-label={`${t('productCompare.page.removeProduct')}: ${product.name}`}
                            onClick={() => removeProduct(product.id)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={data.products.length + 1}>
                      <EmptyState size="compact" title={t('productCompare.page.noDifferences')} />
                    </TableCell>
                  </TableRow>
                )}
                {visibleRows.map((row, index) => {
                  const startsSection = index === 0 || visibleRows[index - 1].section !== row.section;
                  const section = data.sections.find((s) => s.id === row.section);
                  const highlight = row.differs && !row.identifier;
                  return (
                    <React.Fragment key={row.key}>
                      {startsSection && (
                        <TableRow>
                          <TableCell colSpan={data.products.length + 1} sx={{ pt: 2.5, borderBottom: 'none' }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                              {t(`productCompare.sections.${row.section}`, { defaultValue: section?.label ?? row.section })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={highlight ? { bgcolor: tokens.signal.info.row } : undefined}>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            bgcolor: highlight ? tokens.signal.info.row : 'background.paper',
                            color: 'text.secondary',
                            fontWeight: 400,
                          }}
                        >
                          {rowLabel(row)}
                        </TableCell>
                        {row.values.map((cell, cellIndex) => {
                          const better = row.better.includes(cellIndex);
                          return (
                            <TableCell
                              key={data.products[cellIndex].id}
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                color: cell.display === null && cell.value === null ? 'text.disabled' : 'text.primary',
                                ...(better ? { bgcolor: tokens.signal.positive.tint, color: tokens.signal.positive.ink, fontWeight: 600 } : {}),
                              }}
                            >
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {cellText(row, cell)}
                                {better && (
                                  <CheckCircleOutline
                                    sx={{ fontSize: 16 }}
                                    titleAccess={t('productCompare.page.better')}
                                    aria-label={t('productCompare.page.better')}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </DataTableShell>
        </>
      )}
    </PageContainer>
  );
};

export default ProductComparePage;
